import { z } from 'zod';
import type { CountryCode } from 'libphonenumber-js';
import { normalizePhoneToE164 } from '../utils/phone';

const INVALID_PHONE_MESSAGE = 'Enter a valid phone number';

function toE164(value: string, defaultCountry: CountryCode = 'US'): string | null {
  return normalizePhoneToE164(value, defaultCountry);
}

/**
 * Optional phone: empty/whitespace OK; if provided must be valid E.164 after normalization.
 */
export const optionalE164PhoneSchema: z.ZodType<string | undefined> = z
  .union([z.string(), z.literal('')])
  .optional()
  .transform((val): string | undefined => {
    if (val == null || val === '' || val.trim() === '') return undefined;
    return val.trim();
  })
  .pipe(
    z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        if (val == null) return;
        if (!toE164(val)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: INVALID_PHONE_MESSAGE });
        }
      })
      .transform((val): string | undefined => {
        if (val == null) return undefined;
        return toE164(val) ?? undefined;
      }),
  );

/**
 * Required phone: must normalize to valid E.164.
 */
export const requiredE164PhoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required')
  .refine((val) => toE164(val) !== null, { message: INVALID_PHONE_MESSAGE })
  .transform((val) => toE164(val) as string);
